import { useState } from "react";
import { User, Shield, Bell, CreditCard, Terminal, Copy, Plus } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const tabs = [
  { v: "profile", label: "Profile", icon: User },
  { v: "security", label: "Security", icon: Shield },
  { v: "notifications", label: "Notifications", icon: Bell },
  { v: "billing", label: "Billing", icon: CreditCard },
  { v: "api", label: "API", icon: Terminal },
];

function Row({ label, children }) {
  return (
    <div className="flex items-center justify-between py-4 border-b border-white/6 last:border-0">
      <span className="text-sm text-slate-300">{label}</span>
      {children}
    </div>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || "", email: user?.email || "", company: user?.company || "" });
  const [notif, setNotif] = useState({ transaction: true, approval: true, security: true });

  const save = () => toast.success("Profile saved (demo)");
  const copy = (txt) => { navigator.clipboard?.writeText(txt); toast.success("Copied to clipboard"); };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Manage your workspace, security and developer access." showDemo={false} />

      <Tabs defaultValue="profile">
        <TabsList className="bg-white/[0.03] border border-white/8 rounded-full p-1 h-auto flex-wrap gap-1 mb-6">
          {tabs.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.v} value={t.v} className="rounded-full px-4 py-2 text-sm data-[state=active]:bg-[#4F8CFF] data-[state=active]:text-white text-slate-300 gap-1.5" data-testid={`settings-tab-${t.v}`}>
                <Icon className="h-4 w-4" /> {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Profile */}
        <TabsContent value="profile" className="mt-0">
          <Panel className="p-6 max-w-xl">
            <div className="space-y-4">
              <div><Label className="text-slate-300">Name</Label><Input value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="settings-name" /></div>
              <div><Label className="text-slate-300">Email</Label><Input value={profile.email} disabled className="mt-1.5 bg-white/[0.02] border-white/10 text-slate-400" data-testid="settings-email" /></div>
              <div><Label className="text-slate-300">Company</Label><Input value={profile.company} onChange={(e) => setProfile({ ...profile, company: e.target.value })} className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="settings-company" placeholder="Acme AI" /></div>
              <Button onClick={save} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="settings-save-profile">Save changes</Button>
            </div>
          </Panel>
        </TabsContent>

        {/* Security */}
        <TabsContent value="security" className="mt-0">
          <div className="grid lg:grid-cols-2 gap-5">
            <Panel className="p-6">
              <h3 className="text-base font-semibold text-white mb-3">Password</h3>
              <div className="space-y-3">
                <Input type="password" placeholder="Current password" className="bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="settings-current-password" />
                <Input type="password" placeholder="New password" className="bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="settings-new-password" />
                <Button onClick={() => toast.success("Password updated (demo)")} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="settings-update-password">Update password</Button>
              </div>
            </Panel>
            <Panel className="p-6">
              <h3 className="text-base font-semibold text-white mb-1">Two-factor authentication</h3>
              <Row label="2FA via authenticator app">
                <span className="text-xs rounded-full border border-white/12 px-2.5 py-1 text-slate-400">Coming soon</span>
              </Row>
              <Row label="Active sessions">
                <span className="text-sm text-white">2 devices</span>
              </Row>
            </Panel>
          </div>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications" className="mt-0">
          <Panel className="p-6 max-w-xl">
            <Row label="Transaction alerts"><Switch checked={notif.transaction} onCheckedChange={(v) => setNotif({ ...notif, transaction: v })} className="data-[state=checked]:bg-[#4F8CFF]" data-testid="notif-transaction" /></Row>
            <Row label="Approval alerts"><Switch checked={notif.approval} onCheckedChange={(v) => setNotif({ ...notif, approval: v })} className="data-[state=checked]:bg-[#4F8CFF]" data-testid="notif-approval" /></Row>
            <Row label="Security alerts"><Switch checked={notif.security} onCheckedChange={(v) => setNotif({ ...notif, security: v })} className="data-[state=checked]:bg-[#4F8CFF]" data-testid="notif-security" /></Row>
          </Panel>
        </TabsContent>

        {/* Billing */}
        <TabsContent value="billing" className="mt-0">
          <div className="grid lg:grid-cols-3 gap-5">
            <Panel className="p-6">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Plan</p>
              <p className="mt-2 text-xl font-semibold text-white">Early Access</p>
              <span className="mt-2 inline-block text-xs rounded-full border border-[#4F8CFF]/25 bg-[#4F8CFF]/10 px-2.5 py-1 text-[#8fb4ff]">Coming soon</span>
              <p className="mt-3 text-xs text-slate-500">Pricing will be announced as Vvvoraa approaches public availability.</p>
            </Panel>
            <Panel className="p-6">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Usage</p>
              <p className="mt-2 text-xl font-semibold text-white">8 agents · 14 merchants</p>
              <p className="mt-3 text-xs text-slate-500">Demo usage for the current workspace.</p>
            </Panel>
            <Panel className="p-6">
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Payment method</p>
              <p className="mt-2 text-sm text-slate-300">No payment method required during early access.</p>
              <Button variant="outline" onClick={() => toast("Billing opens at launch (demo)")} className="mt-3 bg-transparent border-white/12 text-white hover:bg-white/5 rounded-full">Manage</Button>
            </Panel>
          </div>
        </TabsContent>

        {/* API */}
        <TabsContent value="api" className="mt-0">
          <div className="grid lg:grid-cols-2 gap-5">
            <Panel className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-white">API keys</h3>
                <Button size="sm" onClick={() => toast.success("New key generated (demo)")} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="generate-key-btn"><Plus className="h-3.5 w-3.5" /> New key</Button>
              </div>
              {[
                { name: "Sandbox key", masked: "vora_test_••••••4X9" },
              ].map((k) => (
                <div key={k.name} className="flex items-center justify-between rounded-xl border border-white/8 bg-white/[0.02] px-4 py-3">
                  <div><p className="text-sm text-white">{k.name}</p><p className="text-xs font-mono text-slate-500 mt-0.5">{k.masked}</p></div>
                  <Button size="sm" variant="ghost" onClick={() => copy(k.masked)} className="text-slate-300 hover:text-white hover:bg-white/5 rounded-full gap-1.5"><Copy className="h-3.5 w-3.5" /> Copy</Button>
                </div>
              ))}
              <p className="mt-4 text-xs text-slate-500">Secrets are never exposed in full in the dashboard.</p>
            </Panel>
            <Panel className="p-6">
              <h3 className="text-base font-semibold text-white mb-3">Webhooks</h3>
              <Label className="text-slate-300 text-sm">Endpoint URL</Label>
              <Input placeholder="https://your-app.com/webhooks/vora" className="mt-1.5 bg-white/[0.03] border-white/12 text-white focus-visible:ring-[#4F8CFF]" data-testid="webhook-url" />
              <Button onClick={() => toast.success("Webhook saved (demo)")} className="mt-3 bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full" data-testid="save-webhook-btn">Save webhook</Button>
              <div className="mt-5 pt-5 border-t border-white/8">
                <Row label="Developer mode"><Switch defaultChecked className="data-[state=checked]:bg-[#4F8CFF]" data-testid="developer-mode-toggle" /></Row>
              </div>
            </Panel>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
